import type {
  RoomOccupancy,
  RoomsParticipantDistribution as RoomsParticipantDistributionType,
} from '@/domain/shared/types'

import { useIntl } from '@/intl'
import { emptyRoomIndicesSelector } from '@/scopes/common/components/Filters/ParticipantsLegacy/store'
import * as React from 'react'
import { useRoomsParticipantsDistributionStore } from './useParticipantsStore'

interface Dependencies {
  setSelection: (selection: RoomsParticipantDistributionType) => void
  isFetching: boolean
}

export function useParticipantsFilter({ isFetching, setSelection }: Dependencies) {
  const intl = useIntl()
  const { roomsParticipantDistribution, deleteRooms, setRooms } = useRoomsParticipantsDistributionStore()
  const emptyRoomIndices = emptyRoomIndicesSelector(roomsParticipantDistribution)

  const handleConfirm = () => {
    if (!isFetching) {
      deleteRooms(emptyRoomIndices)
      setSelection(roomsParticipantDistribution)
    }
  }

  const getFormattedText = () => {
    const totalParticipants = roomsParticipantDistribution.reduce(
      (acc, room) => acc + room.adults + room.children.length + room.babies.length,
      0,
    )
    const totalRooms = roomsParticipantDistribution.length
    const desktop = `${totalParticipants} ${intl.formatMessage({
      id: 'quicksearch_filter_participants_people',
    })}, ${totalRooms} ${
      totalRooms > 1
        ? `${intl.formatMessage({
          id: 'quicksearch_filter_participants_room_plural',
        })}`
        : `${intl.formatMessage({
          id: 'quicksearch_filter_participants_room_singular',
        })}`
    }`

    const tablet = `${totalParticipants} ${intl.formatMessage({
      id: 'quicksearch_filter_participants_people',
    })}`

    const mobile = totalParticipants.toString()

    return { desktop, tablet, mobile }
  }

  const showParticipants
    = roomsParticipantDistribution.reduce(
      (acc: number, room: { adults: number, children: Date[], babies: Date[] }) =>
        acc + room.adults + room.children.length + room.babies.length,
      0,
    ) >= 19

  const showChildren
    = roomsParticipantDistribution.reduce(
      (acc: number, room: { children: Date[], babies: Date[] }) => acc + room.children.length + room.babies.length,
      0,
    ) > 0 && !showParticipants

  const warningNoBabiesWithoutAdults = React.useMemo(() => {
    return roomsParticipantDistribution.some((room: RoomOccupancy) => room.adults === 0 && room.babies?.length > 0)
  }, [roomsParticipantDistribution])

  return {
    handleConfirm,
    getFormattedText,
    showParticipants,
    showChildren,
    roomsParticipantDistribution,
    setRooms,
    warningNoBabiesWithoutAdults,
  }
}
